<i18n src="./resources/locales.json"></i18n>
<template>
  <div class="column-filters" :dark="$vuetify.theme.dark">
    <div class="column-filter">
      <div v-for="item in items" :id="item.value" :key="item.value">
        <div class="header-item d-flex flex-row align-center">
          <button :class="item.class" @click="selectFilter(item)">{{ $t(item.text) }}</button>
        </div>
      </div>
    </div>
    <div class="column-filter">
      <div>
        <div v-if="!filter">
          <h3>{{ $t('noFilter') }}</h3>
        </div>
        <div v-else>
          <h3>{{ $t('selectedFilter') }}: {{ $t(filter.name) }}</h3>
          <div v-if="filter.operator">
            <v-checkbox v-model="filter.enabled" :label="$t('enabled')"></v-checkbox>

            <!-- RANGE = 0 -->
            <div v-if="filter.operator.operationType === 0">
              <!-- PERCENTAGE = 0 & NUMBER = 1 -->
              <div v-if="filter.type === 0 || filter.type === 1">
                <input
                  v-model.trim="filter.value[0]"
                  type="number"
                  :required="filter.enabled && !filter.value[1]"
                  class="range number"
                  min="0"
                />
                <span>{{ filter.operator.text.replace('$', $t(filter.shortName)) }}</span>
                <input
                  v-model.trim="filter.value[1]"
                  type="number"
                  :required="filter.enabled && !filter.value[0]"
                  class="range number"
                  min="0"
                />
              </div>
              <!-- DATE = 4 -->
              <div v-else-if="filter.type === 4">
                <input
                  v-model.trim="filter.value[0]"
                  type="datetime-local"
                  :required="filter.enabled && !filter.value[1]"
                  class="range date"
                />
                <span>{{ filter.operator.text.replace('$', $t(filter.shortName)) }}</span>
                <input
                  v-model.trim="filter.value[1]"
                  type="datetime-local"
                  :required="filter.enabled && !filter.value[0]"
                  class="range date"
                />
              </div>
              <!-- TIME = 3 & TIME_FROM_NOW = 5 -->
              <div v-else>
                <input
                  v-model.trim="filter.value[0]"
                  type="text"
                  :required="filter.enabled && !filter.value[1]"
                  class="range time"
                  :pattern="timeRegex"
                  title="HH:MM:SS"
                  placeholder="HH:MM:SS"
                />
                <span>{{ filter.operator.text.replace('$', $t(filter.shortName)) }}</span>
                <input
                  v-model.trim="filter.value[1]"
                  type="text"
                  :required="filter.enabled && !filter.value[0]"
                  class="range time"
                  :pattern="timeRegex"
                  title="HH:MM:SS"
                  placeholder="HH:MM:SS"
                />
              </div>

              <v-checkbox
                v-model="filter.inRange"
                :label="$t('inRange')"
                :value="filter.inRange"
                :disabled="!(filter.value[0] && filter.value[1])"
              ></v-checkbox>
            </div>
            <!-- INCLUDE = 1 -->
            <div v-else-if="filter.operator.operationType === 1">
              <span>INCLUDES</span>

              <div>
                <v-btn @click="toggleSelectAllBP">Toggle All</v-btn>
                <v-divider class="mt-2"></v-divider>
              </div>

              <div v-for="include in filter.includeValues" :key="include">
                <v-checkbox
                  v-model="filter.value"
                  :label="camelCaseToWords(include)"
                  :value="include"
                  class="include"
                ></v-checkbox>
              </div>
            </div>
            <!-- OPEN = 2 -->
            <div v-else-if="filter.operator.operationType === 2">
              <input
                v-model.trim="filter.value"
                type="text"
                :required="filter.enabled"
                class="open"
                :placeholder="$t(filter.shortName)"
              />
            </div>
          </div>
          <div v-else>
            <span>NA FILTER</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { COLUMN_TYPE, OPERATION_TYPE } from '@/constants/table';

export default {
  name: 'EcsFilters',
  props: {
    headers: {
      type: Array,
      required: true
    },
    tab: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      filter: null,
      oldFilterValue: null,
      filterOperators: [
        {
          text: ` <= $ <= `,
          columnTypes: [
            COLUMN_TYPE.NUMBER,
            COLUMN_TYPE.PERCENTAGE,
            COLUMN_TYPE.DATE,
            COLUMN_TYPE.TIME,
            COLUMN_TYPE.TIME_FROM_NOW
          ],
          operationType: OPERATION_TYPE.RANGE
        },
        {
          text: 'filterIncludes',
          columnTypes: [COLUMN_TYPE.INCLUDES],
          operationType: OPERATION_TYPE.INCLUDES
        },
        {
          text: 'filterOpen',
          columnTypes: [COLUMN_TYPE.OPEN],
          operationType: OPERATION_TYPE.OPEN
        }
      ],
      businessProcessColumns: ['activeBp', 'businessProcessName'],
      interactionMediaType: ['Telephony', 'Chat', 'Email', 'SMS'],
      // Might not be accurate
      interactionStateNames: [
        'AgentSelecting',
        'In Conference',
        'Consulting',
        'Offered',
        'Handling',
        'Missed',
        'Rejected',
        'Handled',
        'Wrap Up',
        'Dialing',
        'Delegated To Supervisor',
        'Canceled',
        'Preview'
      ],
      interactionType: [
        'Incoming Telephony',
        'Out. External Telephony',
        'Incoming Chat',
        'Out. External Chat',
        'Incoming Mail',
        'Out. External Mail',
        'Incoming SMS',
        'Out. External SMS'
      ],
      timeRegex: '(([0-9][0-9]):([0-5][0-9]):([0-5][0-9]))'
    };
  },
  computed: {
    ...mapGetters('ORGANIZATION', ['businessProcesses']),

    ...mapGetters('SYSTEM', ['allAgentStates']),

    ...mapGetters('ORGANIZATION', ['skills']),

    items() {
      return this.headers
        .filter(header => header.value !== 'agentName')
        .map(header => {
          if (this.filter && this.filter.valueName === header.value) {
            header.class = 'filter-selected';
          } else if (header.customFilter.enabled) {
            header.class = 'filter-enabled';
          } else if (header.type === COLUMN_TYPE.UNKNOWN) {
            header.class = 'filter-NA';
          } else {
            header.class = '';
          }
          return header;
        })
        .sort((a, b) => {
          return this.$t(a.text).localeCompare(this.$t(b.text), undefined, { numeric: true, sensitivity: 'base' });
        });
    },

    agentStateNames() {
      return [
        ...this.allAgentStates.map(item => {
          return item.name;
        })
      ];
    },

    breakNames() {
      return [
        ...this.availableCustomBreakStates.map(item => {
          return item.name;
        })
      ];
    },

    businessProcessNames() {
      return this.businessProcesses.map(item => {
        return item.name;
      });
    },

    orgSkills() {
      return this.skills.map(skill => skill.name);
    },

    filterValues() {
      return this.filter ? (this.filter.value ? this.filter.value : []) : [];
    },

    filterEnabled() {
      return this.filter ? (this.filter.enabled ? this.filter.enabled : false) : false;
    }
  },

  watch: {
    items() {
      if (this.filter) {
        this.selectFilter(this.items.find(item => item.value === this.filter.valueName));
      }
    },
    filterValues() {
      this.validateData();
    },

    filterEnabled() {
      this.validateData();
    }
  },

  methods: {
    selectFilter(header) {
      if (this.filter) {
        this.filter.class = '';
        this.filter.value = this.oldFilterValue;
      }
      this.filter = header.customFilter;
      this.oldFilterValue = this.filter.value;
      this.filter.valueName = header.value;
      this.filter.name = header.text.replace('headers', 'columns');
      this.filter.shortName = header.text;
      this.filter.operator = this.filterOperators.find(operator => operator.columnTypes.includes(header.type));
      this.filter.type = header.type;
      this.filter.class = 'filter-selected';
      if (!Object.hasOwn(this.filter, 'inRange')) {
        this.filter.inRange = true;
      }
      this.fixNewRange();
      this.changeIncludesValues();
    },

    fixNewRange() {
      // If new Range, force 2 empty values on the array.
      if (
        this.filter.operator &&
        this.filter.operator.operationType === OPERATION_TYPE.RANGE &&
        this.filter.value.length !== 2
      ) {
        this.filter.value = ['', ''];
      }
    },

    changeIncludesValues() {
      if (this.filter.operator && this.filter.operator.operationType === OPERATION_TYPE.INCLUDES) {
        if (this.filter.valueName === 'agentState') {
          this.filter.includeValues = this.agentStateNames;
        } else if (this.businessProcessColumns.includes(this.filter.valueName)) {
          this.filter.includeValues = this.businessProcessNames;
        } else if (this.filter.valueName === 'interactionMediaType') {
          this.filter.includeValues = this.interactionMediaType;
        } else if (this.filter.valueName === 'interactionStateName') {
          this.filter.includeValues = this.interactionStateNames;
        } else if (this.filter.valueName === 'interactionType') {
          this.filter.includeValues = this.interactionType;
        } else if (this.filter.valueName === 'demands') {
          this.filter.includeValues = this.orgSkills;
        }

        this.filter.includeValues.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      }
    },

    validateData() {
      const emptyValue = -1;
      const invalidValue = -2;
      if (this.filter) {
        if (this.filter.enabled) {
          let validateError = false;
          if (this.filter.operator.operationType === OPERATION_TYPE.RANGE) {
            let value1, value2;
            if (this.filter.type === COLUMN_TYPE.TIME || this.filter.type === COLUMN_TYPE.TIME_FROM_NOW) {
              value1 = this.filter.value[0] ? new Date('1970-01-01T' + this.filter.value[0]).getTime() : emptyValue;
              value1 = value1 || invalidValue;
              value2 = this.filter.value[1] ? new Date('1970-01-01T' + this.filter.value[1]).getTime() : emptyValue;
              value2 = value2 || invalidValue;
              if (value1 === invalidValue || value2 === invalidValue) {
                validateError = true;
              }
            } else if (this.filter.type === COLUMN_TYPE.DATE) {
              value1 = this.filter.value[0] ? new Date(this.filter.value[0]).getTime() : -1;
              value2 = this.filter.value[1] ? new Date(this.filter.value[1]).getTime() : -1;
            } else if (this.filter.type === COLUMN_TYPE.NUMBER || this.filter.type === COLUMN_TYPE.PERCENTAGE) {
              value1 = this.filter.value[0] ? Number(this.filter.value[0]) : -1;
              value2 = this.filter.value[1] ? Number(this.filter.value[1]) : -1;
            }

            if (value1 > emptyValue && value2 > emptyValue) {
              if (value1 <= value2) {
                this.$root.$emit('enable-accept');
              } else {
                this.$root.$emit('disable-accept');
              }
            } else if (value1 > emptyValue || value2 > emptyValue) {
              this.$root.$emit('enable-accept');
            } else {
              this.$root.$emit('disable-accept');
            }
          } else if (
            this.filter.operator.operationType === OPERATION_TYPE.INCLUDES ||
            this.filter.operator.operationType === OPERATION_TYPE.OPEN
          ) {
            if (this.filter.value.length > 0) {
              this.$root.$emit('enable-accept');
            } else {
              this.$root.$emit('disable-accept');
            }
          }

          if (validateError) {
            this.$root.$emit('disable-accept');
          }
        } else {
          this.$root.$emit('enable-accept');
        }
      }
    },

    toggleSelectAllBP() {
      this.$nextTick(() => {
        if (!this.filter.value) this.filter.value = [];

        if (this.filter.includeValues.length === this.filter.value.length) {
          this.filter.value = [];
        } else {
          this.filter.value = this.filter.includeValues.slice();
        }
      });
    },

    camelCaseToWords(word) {
      return word.replace(/([A-Z]+)/g, ' $1').replace(/([A-Z][a-z])/g, ' $1');
    }
  }
};
</script>

<style scoped>
.column-filters {
  position: relative;
  height: 100%;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.column-filter {
  overflow-y: scroll;
  padding-left: 5px;
  padding-right: 5px;
}

.range {
  border-style: solid;
  width: 80px;
  text-align: left;
  padding-left: 5px;
}

[dark] .range {
  color: white;
}

.date {
  width: 180px;
}

.time {
  width: 120px;
}

.open {
  border-style: solid;
  width: 80px;
  color: white;
  text-align: left;
  padding-left: 5px;
  width: 150px;
}

.number:invalid,
.date:invalid,
.time:invalid {
  border: 1px solid red;
  box-shadow: 0 0 5px red;
}

.number:valid,
.date:valid,
.time:valid {
  border: 1px solid green;
  box-shadow: 0 0 5px green;
}

.include {
  padding-top: 0;
  margin-top: 0;
}

.filter-selected {
  color: black;
}

[dark] .filter-selected {
  color: white;
}

.filter-enabled {
  color: green;
}

.filter-NA {
  display: none;
}
</style>

<style>
.column-filters .include .v-input__slot {
  margin-top: 8px;
  margin-bottom: 0;
}

.column-filters .include .v-messages {
  display: none;
}
</style>
